/**
 * Footer - Global Footer Component
 *
 * Reusable footer that appears on all pages via BasePage.
 * Contains copyright, links, and other persistent footer content.
 *
 * @example
 * <Footer />
 * <Footer className="bg-gray-50" />
 */
import { Box, Text, type BaseDivProps } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import { useTranslation } from "@/shared/hooks";

export interface FooterProps extends BaseDivProps {
  /** Show border at top */
  showBorder?: boolean;
  /** Maximum width constraint */
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl" | "full";
}

const maxWidthClasses = {
  sm: "max-w-3xl",
  md: "max-w-4xl",
  lg: "max-w-5xl",
  xl: "max-w-7xl",
  "2xl": "max-w-[1600px]",
  full: "max-w-full",
} as const;

/**
 * Footer component
 *
 * Provides a consistent footer across all pages.
 * Includes copyright and responsive layout.
 */
export function Footer({
  showBorder = true,
  maxWidth = "xl",
  className,
  children,
  ...props
}: FooterProps) {
  const t = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      as="footer"
      className={cn(
        "py-8 relative z-5",
        showBorder && "border-t border-gray-200",
        className,
      )}
      {...props}
    >
      <Box
        className={cn(
          maxWidthClasses[maxWidth],
          "mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12",
        )}
      >
        {children || (
          <Box className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <nav className="flex flex-wrap gap-4 text-sm justify-center sm:justify-start">
              <a
                href="/legal/terms"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("termsOfUse")}
              </a>
              <a
                href="/legal/partner-terms"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("partnerTerms")}
              </a>
              <a
                href="/legal/privacy"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("privacyPolicy")}
              </a>
              <a
                href="/legal/tokusho"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("tokushoHo")}
              </a>
            </nav>
            <Text
              variant="caption"
              color="secondary"
              className="text-sm text-center sm:text-right"
            >
              © {currentYear} Apaato. {t("footerRights")}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
}

Footer.displayName = "Footer";
